import { authRequest } from '@/lib/deep-mining/request';

export interface DmclawCreditOverview {
  balance: string;
  today_spend: string;
  total_balance: string;
  total_spend: string;
}

export const getDmclawCreditOverview = async (
  spaceId: string,
): Promise<DmclawCreditOverview | undefined> => {
  return authRequest<DmclawCreditOverview>(
    `/auth/dmclaw/credit/overview?space_id=${encodeURIComponent(spaceId)}`,
  );
};

export interface DmclawCreditSpend {
  total_spend: string;
  space_ids: string[];
}

export const getDmclawCreditSpend = async (): Promise<DmclawCreditSpend> => {
  return authRequest<DmclawCreditSpend>('/auth/dmclaw/credit/spend');
};

export interface DailyStatsItem {
  day: string;
  spend: string;
}

export interface DailyStatsParams {
  space_id: string;
  start_date: string;
  end_date: string;
}

export const getDmclawDailyStats = async (
  params: DailyStatsParams,
): Promise<DailyStatsItem[] | undefined> => {
  return authRequest<DailyStatsItem[]>('/auth/dmclaw/credit/daily_stats', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

export interface ClawItem {
  claw_id: string;
  claw_name: string;
  today_spend: string;
  total_spend: string;
  created_at: string;
  space_name: string;
}

export interface ClawListData {
  total: number;
  page_no: number;
  page_size: number;
  list: ClawItem[];
}

export interface ClawListParams {
  page_no?: number;
  page_size?: number;
  sort_by?:
    | 'claw_name'
    | 'today_spend'
    | 'total_spend'
    | 'created_at'
    | 'space_name';
  sort_order?: 'asc' | 'desc';
}

export const getDmclawClawList = async (
  params: ClawListParams,
): Promise<ClawListData> => {
  return authRequest<ClawListData>('/auth/dmclaw/claw/list', {
    method: 'POST',
    body: JSON.stringify(params),
  });
};

// OctoPush Admin Endpoints

export interface AdminCreditOverviewResponse {
  total_consumed: number;
  total_allocated: number;
  current_points: number;
  total_spend?: string;
  total_balance?: string;
  team_credit?: string;
}

export const getDmclawAdminCreditOverview = async (): Promise<
  AdminCreditOverviewResponse | undefined
> => {
  return authRequest<AdminCreditOverviewResponse>(
    '/auth/dmclaw-admin/credit/overview',
  );
};

export interface MemberItem {
  id: string;
  user_id: string;
  nickname: string;
  account: string;
  spend: string;
  balance: string;
  total_balance: string;
  claw_count: string;
}

export interface AdminMemberListResponse {
  total: number;
  page_no: number;
  page_size: number;
  list: MemberItem[];
}

export interface AdminMemberListParams {
  page_no?: number;
  page_size?: number;
  keyword?: string;
  sort_by?: 'spend' | 'balance' | 'total_balance' | 'claw_count';
  sort_order?: 'asc' | 'desc';
}

export const getDmclawAdminMemberList = async (
  params: AdminMemberListParams,
): Promise<AdminMemberListResponse | undefined> => {
  return authRequest<AdminMemberListResponse>(
    '/auth/dmclaw-admin/member/list',
    {
      method: 'POST',
      body: JSON.stringify({
        page_no: params.page_no ?? 1,
        page_size: params.page_size ?? 20,
        keyword: params.keyword,
        sort_by: params.sort_by ?? 'spend',
        sort_order: params.sort_order ?? 'desc',
      }),
    },
  );
};

export interface AllocateCreditParams {
  user_id: string;
  action: 'add' | 'subtract';
  points: string;
}

interface AllocateCreditResponse {
  status_code: number;
  message: string;
  data: boolean;
}

export interface AllocateResult {
  success: boolean;
  message?: string;
}

export const allocateDmclawCredit = async (
  params: AllocateCreditParams,
): Promise<AllocateResult> => {
  const response = await authRequest<AllocateCreditResponse>(
    '/auth/dmclaw-admin/credit/allocate',
    {
      method: 'POST',
      body: JSON.stringify(params),
      returnAllResponse: true,
      skipErrorHandler: true,
    },
  );
  return {
    success: response?.data ?? false,
    message: response?.message,
  };
};

export const batchAllocateDmclawCredit = async (params: {
  user_ids: string[];
  points: string;
}): Promise<AllocateResult> => {
  const response = await authRequest<AllocateCreditResponse>(
    '/auth/dmclaw-admin/credit/batch_allocate',
    {
      method: 'POST',
      body: JSON.stringify(params),
      returnAllResponse: true,
      skipErrorHandler: true,
    },
  );
  return {
    success: response?.data ?? false,
    message: response?.message,
  };
};

export const allocateAllDmclawCredit = async (params: {
  points: string;
}): Promise<AllocateResult> => {
  const response = await authRequest<AllocateCreditResponse>(
    '/auth/dmclaw-admin/credit/allocate_all',
    {
      method: 'POST',
      body: JSON.stringify(params),
      returnAllResponse: true,
      skipErrorHandler: true,
    },
  );
  return {
    success: response?.data ?? false,
    message: response?.message,
  };
};
